import '../styles/LoansContent.css';
import React, { useEffect, useRef, useState } from 'react';

const GROUPS = [
  {
    id: 'how-it-works',
    title: 'How a Montreaux loan works',
    count: 'Three steps, start to finish',
    panels: [
      {
        kicker: 'Step one',
        title: 'Read the offering',
        body: 'Look over the loan information on this page and decide whether a Montreaux loan fits what you need.',
        note: 'No account needed',
      },
      {
        kicker: 'Step two',
        title: 'Send your application',
        body: 'Fill in one short form: your name, email, phone number, the amount you have in mind, and the loan purpose.',
        note: 'One form, one visit',
      },
      {
        kicker: 'Step three',
        title: 'Your request is recorded',
        body: 'When you submit, Montreaux records your application so your request is not lost when you leave the page.',
        note: 'Confirmation names you',
      },
    ],
  },
  {
    id: 'what-to-bring',
    title: 'What to have ready',
    count: 'Everything on the application form',
    panels: [
      {
        kicker: 'You',
        title: 'Full name',
        body: 'The name you go by in everyday life — it is how the confirmation on the Apply page addresses you.',
        note: 'One field',
      },
      {
        kicker: 'Reach you',
        title: 'Email and phone',
        body: 'An email address and a phone number where Montreaux can reach you about the application you send.',
        note: 'Two fields',
      },
      {
        kicker: 'The loan',
        title: 'Amount and purpose',
        body: 'The amount you would like to request and a short description of what the loan is for.',
        note: 'Two fields',
      },
    ],
  },
];

const Arc = React.memo(function Arc() {
  return (
    <svg
      className="loansc-arc"
      viewBox="0 0 1200 44"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M 6 36 C 220 10, 520 6, 760 20 S 1060 38, 1194 14" />
      <path className="loansc-arc-ghost" d="M 6 36 C 220 10, 520 6, 760 20 S 1060 38, 1194 14" transform="translate(0 5)" />
    </svg>
  );
});

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M4 12h15M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function ApplyCapsule({ label }) {
  return (
    <a className="loansc-capsule" href="/apply">
      <span>{label}</span>
      <Arrow />
    </a>
  );
}

function LoansContent() {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.12 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  let numeral = 0;

  return (
    <section className="loansc-root" ref={ref} aria-labelledby="loansc-title">
      <div className="loansc-rail" data-content-rail="primary">
        <div className="loansc-titleblock">
          <div>
            <p className="loansc-eyebrow">Loan information</p>
            <h1 className="loansc-title" id="loansc-title">
              What a Montreaux loan looks like.
            </h1>
          </div>
          <div className="loansc-aside">
            <p className="loansc-intro">
              Montreaux is a simple loan service for everyday applicants. Everything you need to
              know before applying is gathered here, in plain words — then one short form on the
              Apply page sends your request.
            </p>
          </div>
        </div>

        <Arc />

        {GROUPS.map((group, gi) => (
          <div key={group.id} className={`loansc-group${gi > 0 ? ' loansc-group--offset' : ''}`}>
            {gi > 0 && <Arc />}
            <div className="loansc-group-head">
              <h2 className="loansc-group-title">{group.title}</h2>
              <p className="loansc-group-count">{group.count}</p>
            </div>
            <ul className={`loansc-grid${revealed ? ' loansc-reveal' : ''}`}>
              {group.panels.map((panel) => {
                numeral += 1;
                return (
                  <li className="loansc-panel" key={panel.title}>
                    <div className="loansc-panel-head">
                      <span className="loansc-disc" aria-hidden="true">
                        {numeral}
                      </span>
                      <p className="loansc-kicker">{panel.kicker}</p>
                    </div>
                    <h3 className="loansc-panel-title">{panel.title}</h3>
                    <p className="loansc-panel-body">{panel.body}</p>
                    <p className="loansc-panel-note">{panel.note}</p>
                  </li>
                );
              })}
            </ul>
            <div className="loansc-foot">
              <p className="loansc-foot-text">
                Ready when you are — send the application in one visit.
              </p>
              <ApplyCapsule label="Apply for a loan" />
            </div>
          </div>
        ))}

        <Arc />

        <div className="loansc-foot">
          <p className="loansc-foot-text">
            Prefer to start from the beginning? Montreaux is introduced on the Landing page.
          </p>
          <div className="loansc-foot-actions">
            <a className="loansc-backlink" href="/">
              ← Back to Landing
            </a>
            <ApplyCapsule label="Apply for a loan" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default LoansContent;